import { eventos, ordenCalendario, paginaEventos } from '../data/eventos';
import { grande, mini } from '../utils';
import { precargarImagenes } from './precarga';
import { wa } from './whatsapp';

/** @typedef {import('../data/eventos').Evento} Evento */
/** @typedef {import('../data/eventos').IdEvento} IdEvento */
/** @typedef {import('../data/eventos').VistaFoto} VistaFoto */

/** Desde este ancho la página usa el acomodo de escritorio. */
export const ESCRITORIO = '(min-width: 900px)';

/**
 * Nombre oficial completo de un evento (su título, sin la cursiva).
 * @param {Evento} ev
 * @returns {string}
 */
export const nombreEvento = (ev) => `${ev.titulo.antes}${ev.titulo.em}${ev.titulo.despues || ''}`.trim();

/** Meses del calendario de la página Eventos. */
export const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Ruta de la página de eventos. */
export const RUTA_EVENTOS = '/eventos';

/**
 * Ruta de la página de un evento.
 * @param {Evento} ev
 * @returns {string}
 */
export const rutaEvento = (ev) => `${RUTA_EVENTOS}/${ev.id}`;

/**
 * @param {string} id
 * @returns {Evento|undefined}
 */
export const eventoPorId = (id) => eventos.find((e) => e.id === id);

/**
 * Los eventos con esos id, en ese orden.
 * @param {IdEvento[]} ids
 * @returns {Evento[]}
 */
export const eventosPorId = (ids) => ids.map(eventoPorId).filter((e) => e !== undefined);

/**
 * Los demás eventos (para "Más noches mágicas").
 * @param {IdEvento} id
 * @returns {Evento[]}
 */
export const otrosEventos = (id) => eventos.filter((e) => e.id !== id);

/**
 * Link de WhatsApp del botón de un evento, con su mensaje ya escrito.
 * @param {Evento} ev
 * @returns {string}
 */
export const waEvento = (ev) => wa(ev.waTexto);

/** Link de WhatsApp general de la página Eventos. */
export const WA_EVENTOS = wa(paginaEventos.waTexto);

/**
 * Barra de un evento en el calendario, en porcentaje del año.
 * @typedef {Object} FilaCalendario
 * @property {Evento} evento
 * @property {number} izquierda   % donde empieza
 * @property {number} ancho       % que ocupa
 */

/**
 * Filas del calendario, de la primavera a la Navidad.
 * @returns {FilaCalendario[]}
 */
export const filasCalendario = () => eventosPorId(ordenCalendario).map((ev) => ({
  evento: ev,
  izquierda: (ev.rango[0] / 12) * 100,
  ancho: ((ev.rango[1] - ev.rango[0]) / 12) * 100
}));

/**
 * Las fotos que tocan en esta pantalla: las de todas más las de escritorio o las del celular.
 * @template {{ vista: VistaFoto }} T
 * @param {T[]} fotos
 * @param {boolean} escritorio
 * @returns {T[]}
 */
export const fotosVisibles = (fotos, escritorio) => fotos.filter((f) => f.vista === 'todas' || f.vista === (escritorio ? 'escritorio' : 'movil'));

/**
 * Imágenes que muestra la portada de un evento en esta pantalla: sus polaroids (560 px) y, en escritorio, la foto en
 * diagonal (1600 px).
 * @param {Evento} ev
 * @param {boolean} escritorio
 * @returns {string[]}
 */
export const imagenesPortada = (ev, escritorio) => [
  ...fotosVisibles(ev.fotosPortada, escritorio).map((f) => mini(f.src)),
  ...(escritorio && ev.fondoPortada ? [grande(ev.fondoPortada.src)] : [])
];

/**
 * Descarga de antemano las fotos de la portada de un evento, para que su página abra con ellas listas.
 * @param {Evento} ev
 */
export const precargarEvento = (ev) => precargarImagenes(imagenesPortada(ev, window.matchMedia(ESCRITORIO).matches));

/**
 * Evento de una ruta /eventos/<id>, si la ruta es de un evento.
 * @param {string} pathname
 * @returns {Evento|undefined}
 */
export const eventoDeRuta = (pathname) => {
  const m = pathname.match(/^\/eventos\/([^/]+)\/?$/);
  return m ? eventoPorId(m[1]) : undefined;
};

/**
 * La barra de navegación va clara desde arriba en las páginas con portada de día (Food & Wine y Pixar Fest):
 * sus letras blancas no se verían sobre el fondo claro.
 * @param {string} pathname
 * @returns {boolean}
 */
export const portadaClara = (pathname) => {
  const ev = eventoDeRuta(pathname);
  return !!ev && !ev.tema.oscuro;
};

/** Título de la pestaña del navegador en la página Eventos o en la de un evento. */
export const TITULO_SITIO = 'Experiencias by Jenny';
/**
 * @param {Evento} [ev]
 * @returns {string}
 */
export const tituloPestana = (ev) => (ev ? `${nombreEvento(ev)} · Eventos Disney · ${TITULO_SITIO}` : `Eventos Disney · ${TITULO_SITIO}`);
